import React, { useEffect, useMemo, useState } from "react"
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import clsx from "clsx"
import axios from "axios"
import MyModal from "@common-utils/MyModal"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import { CONST_USER_ROLE_SUPER_ADMIN } from "@common-constants/*"
import ConsultantDetails from "../../modules/business/consultants/details/ConsultantDetails"
import PtfDetails from "../../modules/client/ptf/details/PtfDetails"
import { useAuth } from "../../AppContext"
import { CONST_API_DASHBOARD_KPI_TURNOVER_COUT_MBP_DETAILS_ENDPOINT } from "../../endpoints"

interface Props {
     initialData: any[]
     // Filtres nécessaires au drill-down super-admin (détail par consultant / PTF)
     dateDebut?: any
     dateFin?: any
     personnels?: any
}

interface AtRow {
     consultantId: number
     consultant: string
     responsable: string | null
     cout: number
     ca: number
     ptf: string | null
     ptfId: number | null
}
interface ForfaitRow {
     ptfId: number
     ptf: string | null
     responsable: string | null
     ca: number
     cout: number
     consultants: { id: number; name: string }[]
}
interface PeriodDetail {
     name: string
     period: string
     at: AtRow[]
     forfait: ForfaitRow[]
}

const formatCurrency = (val: number) =>
     new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(val || 0)

const formatMonthName = (dateStr: string) => {
     if (!dateStr || !dateStr.includes("/")) return dateStr
     const [month, year] = dateStr.split("/")
     const date = new Date(parseInt(year), parseInt(month) - 1)
     const formatted = new Intl.DateTimeFormat("fr-FR", { month: "long", year: "numeric" }).format(date)
     return formatted.charAt(0).toUpperCase() + formatted.slice(1)
}

// Normalisation pour la recherche (sans accents, minuscule)
const norm = (s: string) =>
     (s || "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
// Tous les termes doivent être présents (ordre indifférent → "prénom nom" ET "nom prénom" matchent)
const matchAllTokens = (haystack: string, tokens: string[]) => {
     const h = norm(haystack)
     return tokens.every(t => h.includes(t))
}

const ChartRentabilite = ({ initialData, dateDebut, dateFin, personnels }: Props) => {
     const { currentUser } = useAuth()
     const isSuperAdmin = !!currentUser?.roles?.includes(CONST_USER_ROLE_SUPER_ADMIN)

     const [rawData, setRawData] = useState<any[]>(initialData || [])
     const [mensuelOrCumule, setMensuelOrCumule] = useState<"MENSUEL" | "CUMULE">("MENSUEL")
     const [activeTypes, setActiveTypes] = useState<string[]>(["AT", "FORFAIT"])
     const [viewType, setViewType] = useState<"chart" | "list">(window.innerWidth < 992 ? "list" : "chart")

     // Drill-down (super-admin uniquement)
     const [details, setDetails] = useState<PeriodDetail[] | null>(null)
     const [detailLoading, setDetailLoading] = useState(false)
     const [detailError, setDetailError] = useState<string | null>(null)
     const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
     const [detailTab, setDetailTab] = useState<"AT" | "FORFAIT">("AT")
     const [search, setSearch] = useState("")
     const [modalConsultant, setModalConsultant] = useState<{ id: number; name: string } | null>(null)
     const [modalPtf, setModalPtf] = useState<{ id: number; ref: string | null } | null>(null)

     useEffect(() => {
          if (initialData) setRawData(initialData)
     }, [initialData])

     useEffect(() => {
          setDetails(null)
          setDetailError(null)
     }, [initialData, dateDebut, dateFin, personnels])

     // Réinitialise la recherche à chaque changement de mois/onglet
     useEffect(() => {
          setSearch("")
     }, [selectedIndex, detailTab])

     const processedData = useMemo(() => {
          let runningCa = 0
          let runningCout = 0

          return rawData.map(item => {
               let currentCa = 0
               let currentCout = 0

               if (activeTypes.includes("AT")) {
                    currentCa += item.ca_at || 0
                    currentCout += item.cout_at || 0
               }
               if (activeTypes.includes("FORFAIT")) {
                    currentCa += item.ca_forfait || 0
                    currentCout += item.cout_forfait || 0
               }

               if (mensuelOrCumule === "CUMULE") {
                    runningCa += currentCa
                    runningCout += currentCout
                    const mbp = runningCa > 0 ? ((runningCa - runningCout) / runningCa) * 100 : 0
                    return { ...item, ca: runningCa, cout: runningCout, mbp: Math.max(0, parseFloat(mbp.toFixed(2))) }
               }

               const mbp = currentCa > 0 ? ((currentCa - currentCout) / currentCa) * 100 : 0
               return { ...item, ca: currentCa, cout: currentCout, mbp: Math.max(0, parseFloat(mbp.toFixed(2))) }
          })
     }, [rawData, mensuelOrCumule, activeTypes])

     const totals = useMemo(() => {
          if (processedData.length === 0) return { ca: 0, cout: 0, mbp: 0 }
          if (mensuelOrCumule === "CUMULE") {
               const last = processedData[processedData.length - 1]
               return { ca: last.ca, cout: last.cout, mbp: last.mbp }
          }
          const tCa = processedData.reduce((acc, curr) => acc + curr.ca, 0)
          const tCout = processedData.reduce((acc, curr) => acc + curr.cout, 0)
          const avgMbp = processedData.reduce((acc, curr) => acc + curr.mbp, 0) / processedData.length
          return { ca: tCa, cout: tCout, mbp: avgMbp }
     }, [processedData, mensuelOrCumule])

     const toggleMissionType = (type: string) => {
          setActiveTypes(prev => {
               if (prev.includes(type) && prev.length === 1) return prev
               return prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
          })
     }

     const ensureDetails = async () => {
          if (!isSuperAdmin || details || detailLoading) return
          setDetailLoading(true)
          setDetailError(null)
          try {
               const res = await axios.post(CONST_API_DASHBOARD_KPI_TURNOVER_COUT_MBP_DETAILS_ENDPOINT, { dateDebut, dateFin, personnels })
               setDetails(res.data as PeriodDetail[])
          } catch (e) {
               setDetailError("Impossible de charger le détail (accès réservé aux super-administrateurs).")
          } finally {
               setDetailLoading(false)
          }
     }

     const openMonthDetail = (index: number) => {
          if (!isSuperAdmin || index == null || index < 0) return
          setDetailTab("AT")
          setSelectedIndex(index)
          void ensureDetails()
     }

     const handleChartClick = (state: any) => {
          if (state && state.activeTooltipIndex !== undefined && state.activeTooltipIndex !== null) openMonthDetail(state.activeTooltipIndex)
     }

     // Détail agrégé pour le mois cliqué (mensuel = ce mois ; cumulé = de janvier jusqu'à ce mois)
     const selectedDetail = useMemo(() => {
          if (selectedIndex == null || !details) return null
          const detailByName: Record<string, PeriodDetail> = {}
          details.forEach(d => (detailByName[d.name] = d))
          const names = processedData.slice(0, selectedIndex + 1).map(p => p.name)
          const scope = mensuelOrCumule === "CUMULE" ? names : [processedData[selectedIndex]?.name]

          const atMap: Record<number, AtRow> = {}
          const ffMap: Record<number, ForfaitRow> = {}
          scope.forEach(name => {
               const d = detailByName[name]
               if (!d) return
               d.at.forEach(r => {
                    const cur = atMap[r.consultantId] || { ...r, ca: 0, cout: 0 }
                    atMap[r.consultantId] = { ...cur, ca: cur.ca + r.ca, cout: cur.cout + r.cout, consultant: r.consultant, responsable: r.responsable, ptf: r.ptf, ptfId: r.ptfId }
               })
               d.forfait.forEach(r => {
                    const cur = ffMap[r.ptfId] || { ...r, ca: 0, cout: 0, consultants: [] }
                    const seen = new Set(cur.consultants.map(c => c.id))
                    const mergedCons = [...cur.consultants, ...r.consultants.filter(c => !seen.has(c.id))]
                    ffMap[r.ptfId] = { ...cur, ca: cur.ca + r.ca, cout: cur.cout + r.cout, ptf: r.ptf, responsable: r.responsable, consultants: mergedCons }
               })
          })
          const at = Object.values(atMap).sort((a, b) => b.ca - a.ca)
          const forfait = Object.values(ffMap).sort((a, b) => b.ca - a.ca)
          return { name: processedData[selectedIndex]?.name, at, forfait }
     }, [selectedIndex, details, processedData, mensuelOrCumule])

     // Filtrage par recherche (consultant, PTF, responsable) — ordre des termes indifférent
     const filtered = useMemo(() => {
          if (!selectedDetail) return { at: [] as AtRow[], forfait: [] as ForfaitRow[] }
          const tokens = norm(search).split(/\s+/).filter(Boolean)
          if (tokens.length === 0) return { at: selectedDetail.at, forfait: selectedDetail.forfait }
          const at = selectedDetail.at.filter(r => matchAllTokens(`${r.consultant} ${r.ptf ?? ""} ${r.responsable ?? ""}`, tokens))
          const forfait = selectedDetail.forfait.filter(r =>
               matchAllTokens(`${r.ptf ?? ""} ${r.responsable ?? ""} ${r.consultants.map(c => c.name).join(" ")}`, tokens)
          )
          return { at, forfait }
     }, [selectedDetail, search])

     const tabTotals = useMemo(() => {
          const rows: (AtRow | ForfaitRow)[] = detailTab === "AT" ? filtered.at : filtered.forfait
          return { ca: rows.reduce((s, r) => s + r.ca, 0), cout: rows.reduce((s, r) => s + r.cout, 0), count: rows.length }
     }, [filtered, detailTab])

     const cumulLabel = mensuelOrCumule === "CUMULE" ? "Cumulé" : "Mensuel"

     return (
          <div className="d-flex flex-column h-100 position-relative">
               {/* HEADER: Titre & KPI Cards */}
               <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-8 gap-4">
                    <div>
                         <h3 className="fw-bolder text-gray-800 m-0 fs-2">Rentabilité</h3>
                         <span className="text-muted fw-bold fs-7">Suivi du chiffre d'affaires, des charges et de la marge</span>
                    </div>

                    <div className="d-flex flex-wrap w-100 w-md-auto gap-3">
                         <div className="flex-root d-flex flex-column p-3 rounded bg-light-success border border-dashed border-success min-w-100px">
                              <span className="fs-9 text-success fw-boldest text-uppercase fw-bold">CA</span>
                              <span className="fs-7 fw-boldest text-gray-800">{formatCurrency(totals.ca)}</span>
                         </div>
                         <div className="flex-root d-flex flex-column p-3 rounded bg-light-dark border border-dashed border-gray-400 min-w-100px">
                              <span className="fs-9 text-muted fw-boldest text-uppercase fw-bold">Charges</span>
                              <span className="fs-7 fw-boldest text-gray-800">{formatCurrency(totals.cout)}</span>
                         </div>
                         <div className="flex-root d-flex flex-column p-3 rounded bg-light-primary border border-dashed border-primary min-w-80px">
                              <span className="fs-9 text-primary fw-boldest text-uppercase fw-bold">Marge Moy.</span>
                              <span className="fs-7 fw-boldest text-gray-800">{totals.mbp.toFixed(1)}%</span>
                         </div>
                    </div>
               </div>

               {/* TOOLBAR */}
               <div className="d-flex flex-stack flex-wrap gap-4 mb-8">
                    <div className="d-flex align-items-center gap-4 order-2 order-lg-1">
                         <div className="nav-group nav-group-outline bg-light rounded p-0 d-none d-lg-flex">
                              <button
                                   className={clsx("btn btn-icon btn-sm w-35px h-35px", viewType === "chart" ? "bg-white shadow-sm text-primary" : "text-gray-500")}
                                   onClick={() => setViewType("chart")}
                              >
                                   <i className="fas fa-chart-line fs-6"></i>
                              </button>
                              <button
                                   className={clsx("btn btn-icon btn-sm w-35px h-35px", viewType === "list" ? "bg-white shadow-sm text-primary" : "text-gray-500")}
                                   onClick={() => setViewType("list")}
                              >
                                   <i className="fas fa-list fs-6"></i>
                              </button>
                         </div>

                         <div className="d-flex gap-2">
                              <button className={clsx("btn btn-sm fw-bold", activeTypes.includes("AT") ? "btn-primary" : "btn-light")} onClick={() => toggleMissionType("AT")}>
                                   AT
                              </button>
                              <button
                                   className={clsx("btn btn-sm fw-bold", activeTypes.includes("FORFAIT") ? "btn-primary" : "btn-light")}
                                   onClick={() => toggleMissionType("FORFAIT")}
                              >
                                   Forfait
                              </button>
                         </div>
                    </div>

                    <div className="nav-group nav-group-outline bg-light rounded p-0 d-flex order-1 order-lg-2">
                         <button
                              className={clsx("btn btn-sm px-4 fw-bold", mensuelOrCumule === "MENSUEL" ? "bg-white shadow-sm text-primary" : "text-gray-500")}
                              onClick={() => setMensuelOrCumule("MENSUEL")}
                         >
                              Mensuel
                         </button>
                         <button
                              className={clsx("btn btn-sm px-4 fw-bold", mensuelOrCumule === "CUMULE" ? "bg-white shadow-sm text-primary" : "text-gray-500")}
                              onClick={() => setMensuelOrCumule("CUMULE")}
                         >
                              Cumulé
                         </button>
                    </div>
               </div>

               {isSuperAdmin && (
                    <div className="d-flex align-items-center gap-2 text-muted fs-8 mb-3">
                         <i className="fas fa-hand-pointer text-primary" />
                         <span>
                              Cliquez sur un mois pour le détail par consultant (AT) et par PTF (Forfait){mensuelOrCumule === "CUMULE" ? " — cumulé jusqu'au mois cliqué" : ""}.
                         </span>
                    </div>
               )}

               {/* CONTENT */}
               <div className="flex-grow-1 w-100">
                    {viewType === "chart" ? (
                         <div style={{ height: "400px" }}>
                              <ResponsiveContainer width="100%" height="100%">
                                   <ComposedChart
                                        data={processedData}
                                        margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                                        onClick={isSuperAdmin ? handleChartClick : undefined}
                                        style={isSuperAdmin ? { cursor: "pointer" } : undefined}
                                   >
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                        <XAxis dataKey="name" fontSize={11} tickLine={false} axisLine={false} tick={{ fill: "#7E8299" }} dy={10} />
                                        <YAxis
                                             yAxisId="left"
                                             orientation="left"
                                             stroke="#34bfa3"
                                             fontSize={11}
                                             tickFormatter={val => `${(val / 1000).toFixed(0)}k`}
                                             axisLine={false}
                                             tickLine={false}
                                        />
                                        <YAxis
                                             yAxisId="right"
                                             orientation="right"
                                             stroke="#36a3f7"
                                             fontSize={11}
                                             tickFormatter={val => `${val}%`}
                                             axisLine={false}
                                             tickLine={false}
                                        />

                                        <Tooltip
                                             contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 5px 15px rgba(0,0,0,0.1)", zIndex: 1000 }}
                                             labelFormatter={label => formatMonthName(label)}
                                             formatter={(value: any, name: any) => {
                                                  const valFormatted = new Intl.NumberFormat("fr-FR").format(value)
                                                  const displayName = name ?? "Donnée"
                                                  return [displayName === "% Marge" ? `${valFormatted}%` : `${valFormatted} €`, displayName]
                                             }}
                                        />

                                        <Legend wrapperStyle={{ paddingTop: "25px" }} />
                                        <Bar yAxisId="left" name="Chiffre d'Affaires" dataKey="ca" fill="#34bfa3" radius={[4, 4, 0, 0]} barSize={30} />
                                        <Bar yAxisId="left" name="Charges" dataKey="cout" fill="#43433E" radius={[4, 4, 0, 0]} barSize={30} />
                                        <Line yAxisId="right" name="% Marge" type="monotone" dataKey="mbp" stroke="#36a3f7" strokeWidth={3} dot={{ r: 4, fill: "#36a3f7" }} />
                                   </ComposedChart>
                              </ResponsiveContainer>
                         </div>
                    ) : (
                         <div className="row g-3 overflow-auto pe-2" style={{ maxHeight: "550px" }}>
                              {processedData.map((item, idx) => (
                                   <div key={idx} className="col-12 col-md-6 col-xl-4">
                                        <div
                                             className={clsx("card shadow-none border border-gray-200 p-4 h-100", isSuperAdmin && "bg-hover-light cursor-pointer")}
                                             onClick={() => isSuperAdmin && openMonthDetail(idx)}
                                        >
                                             <div className="d-flex justify-content-between align-items-center mb-3">
                                                  <span className="fw-bolder fs-5 text-gray-800">{formatMonthName(item.name)}</span>
                                                  <span className={clsx("badge fw-bold", item.mbp >= 25 ? "badge-light-success text-success" : "badge-light-danger text-danger")}>
                                                       Marge: {item.mbp}%
                                                  </span>
                                             </div>
                                             <div className="d-flex justify-content-between">
                                                  <div className="d-flex flex-column">
                                                       <span className="text-muted fs-8 fw-bold">CA</span>
                                                       <span className="fw-bold text-success">{formatCurrency(item.ca)}</span>
                                                  </div>
                                                  <div className="d-flex flex-column text-end">
                                                       <span className="text-muted fs-8 fw-bold">CHARGES</span>
                                                       <span className="fw-bold text-dark">{formatCurrency(item.cout)}</span>
                                                  </div>
                                             </div>
                                        </div>
                                   </div>
                              ))}
                         </div>
                    )}
               </div>

               {/* MODALE DÉTAIL (super-admin) — ne se ferme pas tant qu'une sous-modale (consultant/PTF) est ouverte */}
               <MyModal
                    title={selectedDetail?.name ? `Détail ${cumulLabel} — ${formatMonthName(selectedDetail.name)}` : "Détail"}
                    show={selectedIndex !== null}
                    onCloseCallback={() => {
                         if (!modalConsultant && !modalPtf) setSelectedIndex(null)
                    }}
                    size="xl"
               >
                    {detailLoading ? (
                         <div className="d-flex flex-column align-items-center py-15">
                              <MySimpleSpinner size="lg" />
                              <span className="text-muted fw-bold mt-3">Chargement du détail…</span>
                         </div>
                    ) : detailError ? (
                         <div className="alert alert-danger d-flex align-items-center">
                              <i className="fas fa-exclamation-triangle text-danger me-3" />
                              {detailError}
                         </div>
                    ) : selectedDetail ? (
                         <div className="d-flex flex-column" style={{ minHeight: "60vh" }}>
                              {/* Onglets AT / Forfait */}
                              <ul className="nav nav-tabs nav-line-tabs mb-4 fs-6 fw-bold">
                                   <li className="nav-item">
                                        <button className={clsx("nav-link", detailTab === "AT" && "active")} onClick={() => setDetailTab("AT")}>
                                             <i className="fas fa-user-tie me-2" /> AT — par consultant
                                             <span className="badge badge-light-primary ms-2">{selectedDetail.at.length}</span>
                                        </button>
                                   </li>
                                   <li className="nav-item">
                                        <button className={clsx("nav-link", detailTab === "FORFAIT" && "active")} onClick={() => setDetailTab("FORFAIT")}>
                                             <i className="fas fa-briefcase me-2" /> Forfait — par PTF
                                             <span className="badge badge-light-primary ms-2">{selectedDetail.forfait.length}</span>
                                        </button>
                                   </li>
                              </ul>

                              {/* Recherche */}
                              <div className="input-group input-group-solid mb-4">
                                   <span className="input-group-text">
                                        <i className="fas fa-magnifying-glass fs-7" />
                                   </span>
                                   <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Rechercher par consultant, PTF ou responsable (nom prénom / prénom nom)…"
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        autoFocus
                                   />
                                   {search && (
                                        <span className="input-group-text cursor-pointer" onClick={() => setSearch("")}>
                                             <i className="fas fa-times fs-8 text-muted" />
                                        </span>
                                   )}
                              </div>

                              {/* Table (scrollable) */}
                              <div className="table-responsive flex-grow-1" style={{ maxHeight: "46vh", overflowY: "auto" }}>
                                   {detailTab === "AT" ? (
                                        filtered.at.length === 0 ? (
                                             <div className="text-muted fst-italic py-8 text-center">Aucun consultant en régie sur cette période.</div>
                                        ) : (
                                             <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-3">
                                                  <thead className="position-sticky top-0 bg-white" style={{ zIndex: 1 }}>
                                                       <tr className="fw-bolder text-muted fs-8 text-uppercase">
                                                            <th className="min-w-200px">Consultant</th>
                                                            <th className="min-w-150px">Responsable</th>
                                                            <th className="min-w-120px">PTF</th>
                                                            <th className="text-end min-w-100px">Charges</th>
                                                            <th className="text-end min-w-100px">CA</th>
                                                       </tr>
                                                  </thead>
                                                  <tbody>
                                                       {filtered.at.map(r => (
                                                            <tr key={r.consultantId}>
                                                                 <td>
                                                                      <span
                                                                           className="fw-bold text-gray-800 text-hover-primary cursor-pointer"
                                                                           onClick={() => setModalConsultant({ id: r.consultantId, name: r.consultant })}
                                                                      >
                                                                           {r.consultant}
                                                                      </span>
                                                                 </td>
                                                                 <td className="text-gray-600">{r.responsable ?? "—"}</td>
                                                                 <td>
                                                                      {r.ptf ? (
                                                                           <span
                                                                                className={clsx("badge badge-light-primary fw-bold", r.ptfId && "cursor-pointer text-hover-primary")}
                                                                                onClick={() => r.ptfId && setModalPtf({ id: r.ptfId, ref: r.ptf })}
                                                                           >
                                                                                {r.ptf}
                                                                           </span>
                                                                      ) : (
                                                                           <span className="text-muted">—</span>
                                                                      )}
                                                                 </td>
                                                                 <td className="text-end fw-semibold text-gray-700">{formatCurrency(r.cout)}</td>
                                                                 <td className="text-end fw-bolder text-success">{formatCurrency(r.ca)}</td>
                                                            </tr>
                                                       ))}
                                                  </tbody>
                                             </table>
                                        )
                                   ) : filtered.forfait.length === 0 ? (
                                        <div className="text-muted fst-italic py-8 text-center">Aucun forfait sur cette période.</div>
                                   ) : (
                                        <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-3">
                                             <thead className="position-sticky top-0 bg-white" style={{ zIndex: 1 }}>
                                                  <tr className="fw-bolder text-muted fs-8 text-uppercase">
                                                       <th className="min-w-120px">PTF</th>
                                                       <th className="min-w-150px">Responsable</th>
                                                       <th className="min-w-250px">Consultants rattachés</th>
                                                       <th className="text-end min-w-100px">Charges</th>
                                                       <th className="text-end min-w-100px">CA</th>
                                                  </tr>
                                             </thead>
                                             <tbody>
                                                  {filtered.forfait.map(r => (
                                                       <tr key={r.ptfId}>
                                                            <td>
                                                                 {r.ptf ? (
                                                                      <span
                                                                           className="badge badge-light-primary fw-bold cursor-pointer text-hover-primary"
                                                                           onClick={() => setModalPtf({ id: r.ptfId, ref: r.ptf })}
                                                                      >
                                                                           {r.ptf}
                                                                      </span>
                                                                 ) : (
                                                                      <span className="text-muted">—</span>
                                                                 )}
                                                            </td>
                                                            <td className="text-gray-600">{r.responsable ?? "—"}</td>
                                                            <td>
                                                                 {r.consultants.length === 0 ? (
                                                                      <span className="text-muted fst-italic fs-8">Projet — aucun consultant</span>
                                                                 ) : (
                                                                      <div className="d-flex flex-wrap gap-2">
                                                                           {r.consultants.map(c => (
                                                                                <span
                                                                                     key={c.id}
                                                                                     className="badge badge-light text-gray-700 text-hover-primary cursor-pointer"
                                                                                     onClick={() => setModalConsultant({ id: c.id, name: c.name })}
                                                                                >
                                                                                     <i className="fas fa-user fs-9 me-1" /> {c.name}
                                                                                </span>
                                                                           ))}
                                                                      </div>
                                                                 )}
                                                            </td>
                                                            <td className="text-end fw-semibold text-gray-700">{formatCurrency(r.cout)}</td>
                                                            <td className="text-end fw-bolder text-success">{formatCurrency(r.ca)}</td>
                                                       </tr>
                                                  ))}
                                             </tbody>
                                        </table>
                                   )}
                              </div>

                              {/* TOTAUX — toujours visibles, en bas à droite */}
                              <div className="d-flex justify-content-end align-items-center gap-3 border-top border-gray-200 pt-4 mt-2">
                                   <span className="text-muted fs-8 fw-bold me-auto">
                                        {tabTotals.count} ligne{tabTotals.count > 1 ? "s" : ""}
                                        {search ? " (filtré)" : ""}
                                   </span>
                                   <div className="d-flex align-items-center gap-2">
                                        <span className="text-muted fs-8 fw-bold text-uppercase">Charges ({detailTab === "AT" ? "AT" : "Forfait"})</span>
                                        <span className="fs-4 fw-boldest text-gray-800">{formatCurrency(tabTotals.cout)}</span>
                                   </div>
                                   <div className="separator separator-vertical h-25px" />
                                   <div className="d-flex align-items-center gap-2">
                                        <span className="text-success fs-8 fw-bold text-uppercase">CA ({detailTab === "AT" ? "AT" : "Forfait"})</span>
                                        <span className="fs-3 fw-boldest text-success">{formatCurrency(tabTotals.ca)}</span>
                                   </div>
                              </div>
                         </div>
                    ) : null}
               </MyModal>

               {/* MODALE CONSULTANT (au-dessus, ferme uniquement elle-même via Échap) */}
               <MyModal title={modalConsultant?.name} show={modalConsultant !== null} onCloseCallback={() => setModalConsultant(null)} fullscreen>
                    {modalConsultant !== null && <ConsultantDetails idConsultantProp={modalConsultant.id} />}
               </MyModal>

               {/* MODALE PTF (au-dessus, ferme uniquement elle-même via Échap) */}
               <MyModal title={modalPtf?.ref ? `PTF — ${modalPtf.ref}` : "PTF"} show={modalPtf !== null} onCloseCallback={() => setModalPtf(null)} fullscreen>
                    {modalPtf !== null && <PtfDetails idPtfProp={modalPtf.id} />}
               </MyModal>
          </div>
     )
}

export default ChartRentabilite
